/**
 * Motion layer, GSAP (+ ScrollTrigger, SplitText) and Lenis.
 *
 * The page is COMPLETE and readable before this module runs. The stylesheet no
 * longer hides anything: every initial hidden state is applied here by gsap.set()
 * immediately before the tween that undoes it. A bundle that never loads costs
 * the animation, never the content, for a visitor or for a crawler.
 */
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger, SplitText);

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;

// The markup clears the fixed header with `scroll-mt-24` (6rem). scroll-margin-top
// is a property of NATIVE fragment navigation; Lenis owns the scroll position and
// never reads it, so the same value has to travel through its scroll API or every
// in-page anchor lands with the section hidden under the header.
const HEADER_OFFSET = 96;

/**
 * Resolve a fragment to its element. Deliberately strict: `href="#"` (a
 * non-target used as a button) is an invalid selector and would throw, killing
 * the whole init pass.
 */
function hashTarget(hash) {
  if (/^#[A-Za-z][\w-]*$/.test(hash) === false) return null;
  return document.querySelector(hash);
}

/** The element a link points at, but only when the link stays on this page. */
function sameDocumentTarget(link) {
  const url = new URL(link.href, window.location.href);
  if (url.pathname !== window.location.pathname) return null;
  return hashTarget(url.hash);
}

/**
 * Run once the images have resolved. Anything that measures rendered geometry, 
 * an anchor's document offset shifts as late-loading images above it grow the
 * page, has to wait for `load`, or it measures a layout that is about to move.
 */
function whenLoaded(run) {
  if (document.readyState === 'complete') {
    run();
    return;
  }
  window.addEventListener('load', run, { once: true });
}

/**
 * A node the visitor can ALREADY see must not be hidden. The reveal state is
 * applied after first paint, so hiding painted content would make it blink, 
 * the very flash the deleted inline script in index.ejs existed to paper over.
 * Above the fold the page simply arrives; the motion starts below it.
 */
function isAlreadyVisible(el) {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return el.getBoundingClientRect().top < viewportHeight;
}

/** Legacy `.delay-1…5` ramp, kept so the designed reveal rhythm survives. */
function revealDelay(el) {
  for (let step = 1; step <= 5; step += 1) {
    if (el.classList.contains(`delay-${step}`) === true) return step * 0.06;
  }
  return 0;
}

/**
 * Absolute document offset of an anchor target, minus the header.
 *
 * Handing Lenis the ELEMENT instead resolves against its own scroll bookkeeping,
 * which is stale right after the browser's native fragment jump, measured, it
 * landed a full header-height too high. A number is unambiguous.
 */
function anchorDestination(target) {
  return target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
}

function initLenis() {
  const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add(time => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // Both anchor paths have to be re-implemented, or navigation that works today
  // breaks: the in-page click, and the hash already present on arrival, every
  // one of the other 29 pages links the homepage sections as `./#pricing`.
  document.querySelectorAll('a[href*="#"]').forEach(link => {
    if (sameDocumentTarget(link) === null) return;
    link.addEventListener('click', event => {
      const target = sameDocumentTarget(link);
      if (target === null) return;
      event.preventDefault();
      lenis.scrollTo(anchorDestination(target));
    });
  });

  // Re-anchor on arrival AFTER load, not on the next frame: the browser's own
  // fragment jump happens mid-parse, and every image that resolves above the
  // target afterwards pushes it further down the page. Measured once the layout
  // has settled, the section lands where it should.
  const arrivalTarget = hashTarget(window.location.hash);
  if (arrivalTarget !== null) {
    whenLoaded(() => lenis.scrollTo(anchorDestination(arrivalTarget), { immediate: true }));
  }
}

function initSplitLines() {
  document.querySelectorAll('[data-anim="split-lines"]').forEach(el => {
    if (isAlreadyVisible(el) === true) return;
    SplitText.create(el, {
      type: 'lines',
      linesClass: 'anim-line',
      // The lines rise out of a clipped wrapper; without the mask they are simply
      // parked on top of the paragraph below until their trigger fires.
      mask: 'lines',
      // Re-split when the fonts land or the box is resized, a split measured on
      // fallback metrics breaks the heading at the wrong words.
      autoSplit: true,
      // aria:'auto' labels the heading and hides the fragments: without it a
      // screen reader announces a three-line h1 as three unrelated texts.
      aria: 'auto',
      onSplit: self =>
        gsap.from(self.lines, {
          yPercent: 130,
          duration: 1,
          ease: 'power4.out',
          stagger: 0.1,
          scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        }),
    });
  });
}

function initReveals() {
  // `.animate-on-scroll` is the legacy hook carried by 28 templates. Nodes that
  // opt into a richer animation through data-anim are excluded so nothing is
  // driven by two tweens at once.
  const nodes = document.querySelectorAll(
    '[data-anim="fade-up"], .animate-on-scroll:not([data-anim])'
  );
  nodes.forEach(el => {
    if (isAlreadyVisible(el) === true) return;
    const isScaleIn = el.classList.contains('scale-in') === true;
    gsap.set(el, isScaleIn === true ? { opacity: 0, scale: 0.95 } : { opacity: 0, y: 24 });
    gsap.to(el, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.6,
      ease: 'power3.out',
      delay: revealDelay(el),
      scrollTrigger: { trigger: el, start: 'top 80%', once: true },
    });
  });
}

function initStagger() {
  document.querySelectorAll('[data-stagger], [data-stagger-scale]').forEach(group => {
    if (isAlreadyVisible(group) === true) return;
    const children = Array.from(group.children);
    if (children.length === 0) return;
    const isScale = group.hasAttribute('data-stagger-scale');
    gsap.set(children, isScale === true ? { opacity: 0, scale: 0.97 } : { opacity: 0, y: 14 });
    gsap.to(children, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.45,
      ease: 'power3.out',
      stagger: 0.06,
      scrollTrigger: { trigger: group, start: 'top 80%', once: true },
    });
  });
}

function initCounters() {
  document.querySelectorAll('[data-count]').forEach(el => {
    const target = Number(el.getAttribute('data-count'));
    if (Number.isNaN(target) === true) return;
    // The element already renders its final value in the HTML, unit included
    // ("100 %"). Only the leading number is animated and the rest is kept
    // verbatim, rewriting textContent from the number alone would silently drop
    // the unit for anyone who arrives mid-count.
    const suffix = el.textContent.replace(/^\s*\d+/, '');
    const counter = { value: 0 };
    gsap.to(counter, {
      value: target,
      duration: 1.2,
      snap: { value: 1 },
      ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 85%', once: true },
      onUpdate: () => {
        el.textContent = String(Math.round(counter.value)) + suffix;
      },
    });
  });
}

function initParallax() {
  document.querySelectorAll('[data-parallax]').forEach(el => {
    const depth = Number(el.getAttribute('data-parallax'));
    if (Number.isNaN(depth) === true) return;
    gsap.to(el, { y: depth * 400, ease: 'none', scrollTrigger: { trigger: el, scrub: true } });
  });
}

function initTilt() {
  document.querySelectorAll('[data-tilt]').forEach(el => {
    gsap.set(el, { transformPerspective: 1000 });
    el.addEventListener('mousemove', event => {
      const rect = el.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      gsap.to(el, { rotateY: px * 6, rotateX: -py * 6, duration: 0.5, ease: 'power2.out' });
    });
    el.addEventListener('mouseleave', () => {
      gsap.to(el, { rotateY: 0, rotateX: 0, duration: 0.6, ease: 'power2.out' });
    });
  });
}

export function initAnimations() {
  // Nothing to reveal (the content is already visible) and nothing to smooth.
  // Lenis is not instantiated at all, so the scroll stays native.
  if (REDUCE === true) return;

  initLenis();
  initReveals();
  initStagger();
  initCounters();
  initParallax();
  initTilt();

  // SplitText measures line boxes. Splitting before the webfonts land breaks the
  // heading on fallback metrics; the refresh re-anchors every trigger the new
  // line wrappers moved.
  document.fonts.ready.then(() => {
    initSplitLines();
    ScrollTrigger.refresh();
  });
}
